import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AudioState, Audio } from '../../types/audioTypes'
import {
  fetchAudiosThunk,
  createAudioThunk,
  deleteAudioThunk,
  updateAudioThunk,
} from './audioThunk'

const initialState: AudioState = {
  audioPodcasts: [],
  currentAudio: null,
  currentIndex: null,
  isLoading: false,
  error: null,
  isPlaying: false,
  playBackText: '',
}

const audioSlice = createSlice({
  name: 'audio',
  initialState,
  reducers: {
    setCurrentAudio: (state, action: PayloadAction<Audio>) => {
      state.currentAudio = action.payload
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload
    },
    setPlayBackText: (
      state,
      action: PayloadAction<'' | 'Playing' | 'Paused'>
    ) => {
      state.playBackText = action.payload
    },
    updateProgress: (
      state,
      action: PayloadAction<{ id: string; progress: number }>
    ) => {
      const { id, progress } = action.payload
      const audioIndex = state.audioPodcasts.findIndex(
        (audio: Audio) => audio.id === id
      )
      if (audioIndex !== -1) {
        state.audioPodcasts[audioIndex].progress = progress
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAudiosThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        fetchAudiosThunk.fulfilled,
        (state, action: PayloadAction<Audio[]>) => {
          state.audioPodcasts = action.payload || []
          state.isLoading = false
        }
      )
      .addCase(fetchAudiosThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch audio podcasts'
      })
      .addCase(createAudioThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        createAudioThunk.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.audioPodcasts.push(action.payload)
        }
      )
      .addCase(createAudioThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to create audio'
      })
      .addCase(updateAudioThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        updateAudioThunk.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (action.payload) {
            const audio = action.payload
            const index = state.audioPodcasts.findIndex(
              (a) => a.id === audio.id
            )
            if (index !== -1) {
              state.audioPodcasts[index] = {
                ...state.audioPodcasts[index],
                ...audio,
              }
            }
          }
          state.isLoading = false
        }
      )
      .addCase(updateAudioThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to update audio'
      })
      .addCase(deleteAudioThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        deleteAudioThunk.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.audioPodcasts = state.audioPodcasts.filter(
            (audio) => audio.id !== action.payload
          )
          state.isLoading = false
        }
      )
      .addCase(deleteAudioThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to delete audio'
      })
  },
})

export const {
  setCurrentAudio,
  updateProgress,
  setIsPlaying,
  setPlayBackText,
} = audioSlice.actions

export default audioSlice.reducer
